/**
 * QuickActionsPopover - Dropdown de Ações Rápidas via Atalho "/"
 * 
 * Exibe lista de regras manuais (condition_type='manual_execution')
 * quando usuário digita "/" no input de mensagens.
 * Similar ao envio de mensagens rápidas do WhatsApp Business.
 * 
 * Arquitetura multi-provider: Este componente é agnóstico ao provider,
 * chamando a edge function routing-engine que roteia internamente.
 * 
 * @version 1.0.0
 * @date 2026-01-20
 */

import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { RoutingCategoryIcon } from '@/components/tenant/routing/categories/RoutingCategoryIcon';
import { Loader2, Play, Zap, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { showToast } from '@/lib/toast';
import { createLogger } from '@/lib/logger';

// Logger específico para este módulo
const log = createLogger('QuickActionsPopover');

// Interface para regra manual retornada pela RPC get_manual_routing_rules
// Atualizada em 2026-06-23 para incluir colunas de categoria.
interface ManualRule {
  id: string;
  name: string;
  description: string | null;
  created_by: string;
  visibility: string;
  is_owner: boolean;
  priority: number;
  stop_on_match: boolean;
  action_type: string;
  action_value: Record<string, unknown>;
  category_id: string | null;
  category_name: string | null;
  category_color: string | null;
  category_icon: string | null;
  category_visibility: string | null;
  category_sort_order: number | null;
}

// Props do componente
interface QuickActionsPopoverProps {
  /** Controla se o popover está aberto */
  open: boolean;
  /** Callback para mudar estado de abertura */
  onOpenChange: (open: boolean) => void;
  /** Texto de busca (parte após "/") para filtrar ações */
  searchQuery: string;
  /** ID do tenant atual */
  tenantId: string;
  /** ID da inbox da conversa */
  inboxId: string;
  /** ID da conversa atual */
  conversationId: string;
  /** ID do contato da conversa */
  contactId: string;
  /** Telefone do contato */
  contactPhone: string;
  /** Nome do contato (opcional) */
  contactName?: string | null;
  /** Modo de resposta para comentários Instagram (opcional) */
  replyMode?: 'direct' | 'comment_public' | 'comment_private';
  /** ID do comentário Instagram sendo respondido (opcional) */
  commentId?: string;
  /** ID interno da mensagem sendo respondida (para exibir balão de citação) */
  replyToMessageId?: string;
  /** Callback executado após ação ser realizada com sucesso */
  onExecuted: () => void;
  /** Elemento children que será o trigger do popover */
  children: React.ReactNode;
}

/**
 * Componente de Popover para Ações Rápidas
 * 
 * Funciona como um command palette que aparece ao digitar "/"
 * no input de mensagens, listando as ações rápidas do usuário.
 */
export function QuickActionsPopover({
  open,
  onOpenChange,
  searchQuery,
  tenantId,
  inboxId,
  conversationId,
  contactId,
  contactPhone,
  contactName,
  replyMode,
  commentId,
  replyToMessageId,
  onExecuted,
  children,
}: QuickActionsPopoverProps) {
  // Estado para controlar qual regra está sendo executada
  const [executingRuleId, setExecutingRuleId] = useState<string | null>(null);

  // Busca as regras manuais do usuário atual
  // Mesma query usada no ManualRoutingSection para consistência
  const { data: rules, isLoading, error } = useQuery({
    queryKey: ['quick-action-rules', tenantId, inboxId],
    queryFn: async () => {
      log.debug('Buscando regras de ação rápida via RPC', { tenantId, inboxId });

      // Usa a mesma RPC do sidebar para respeitar visibilidade (private/shared/public)
      const { data, error } = await supabase.rpc('get_manual_routing_rules', {
        p_inbox_id: inboxId,
      });

      if (error) {
        log.error('Erro ao buscar regras via RPC', { error });
        throw error;
      }

      log.debug('Regras encontradas via RPC', { total: data?.length });
      return (data || []) as ManualRule[];
    },
    enabled: open && !!tenantId && !!inboxId,
    staleTime: 30000, // Cache por 30 segundos
  });

  // Filtra regras pelo texto de busca (case insensitive)
  const filteredRules = useMemo(() => {
    if (!rules) return [];
    if (!searchQuery.trim()) return rules;
    
    const query = searchQuery.toLowerCase();
    return rules.filter(rule => 
      rule.name.toLowerCase().includes(query) ||
      rule.description?.toLowerCase().includes(query)
    );
  }, [rules, searchQuery]);

  // Agrupa por categoria (2026-06-23): mantém ordem por sort_order da
  // categoria, "Sem categoria" sempre no fim.
  interface PopoverGroup {
    key: string;
    name: string;
    color: string | null;
    icon: string | null;
    sortOrder: number;
    rules: ManualRule[];
  }
  const groupedRules = useMemo<PopoverGroup[]>(() => {
    const map = new Map<string, PopoverGroup>();
    for (const r of filteredRules) {
      const key = r.category_id ?? '__none__';
      if (!map.has(key)) {
        map.set(key, {
          key,
          name: r.category_name ?? 'Sem categoria',
          color: r.category_color,
          icon: r.category_icon,
          sortOrder: r.category_sort_order ?? Number.MAX_SAFE_INTEGER - 1,
          rules: [],
        });
      }
      map.get(key)!.rules.push(r);
    }
    const arr = Array.from(map.values());
    arr.sort((a, b) => {
      if (a.key === '__none__') return 1;
      if (b.key === '__none__') return -1;
      if (a.sortOrder !== b.sortOrder) return a.sortOrder - b.sortOrder;
      return a.name.localeCompare(b.name);
    });
    return arr;
  }, [filteredRules]);


  /**
   * Executa uma regra manual de ação rápida
   * Chama a edge function routing-engine com os dados do contexto
   */
  const handleExecuteRule = async (rule: ManualRule) => {
    log.info('Executando ação rápida', { ruleId: rule.id, ruleName: rule.name });
    setExecutingRuleId(rule.id);

    try {
      // Monta payload incluindo contexto de reply para comentários Instagram
      const payload: Record<string, unknown> = {
        action: 'execute_manual',
        rule_id: rule.id,
        tenant_id: tenantId,
        inbox_id: inboxId,
        conversation_id: conversationId,
        contact_id: contactId,
        contact_phone: contactPhone,
        contact_name: contactName || undefined,
      };

      // Propaga contexto de reply para comentários Instagram (se presente)
      if (replyMode) {
        payload.reply_mode = replyMode;
      }
      if (commentId) {
        payload.comment_id = commentId;
      }
      // Propaga ID da mensagem respondida para exibir balão de citação
      if (replyToMessageId) {
        payload.reply_to_message_id = replyToMessageId;
      }

      log.debug('Payload da ação rápida', { replyMode, commentId });

      const { data, error } = await supabase.functions.invoke('routing-engine', {
        body: payload,
      });

      if (error) {
        log.error('Erro na edge function', { error });
        throw error;
      }

      if (data?.success) {
        log.info('Ação executada com sucesso', { 
          ruleId: rule.id, 
          messagesSent: data.messages_sent 
        });
        
        showToast.success(
          `Ação "${rule.name}" executada!`,
          data.messages_sent > 0 
            ? `${data.messages_sent} mensagem(s) enviada(s)` 
            : 'Ações aplicadas com sucesso'
        );
        
        // Callback para limpar input e fechar popover
        onExecuted();
      } else {
        throw new Error(data?.error || 'Erro desconhecido ao executar ação');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      log.error('Falha ao executar ação', { ruleId: rule.id, error: errorMessage });
      showToast.error('Erro ao executar ação', errorMessage);
    } finally {
      setExecutingRuleId(null);
    }
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      {/* Trigger invisível - controlado externamente */}
      <PopoverTrigger asChild>
        {children}
      </PopoverTrigger>
      
      <PopoverContent 
        className="w-80 p-0 bg-popover border shadow-lg" 
        side="top" 
        align="start"
        sideOffset={8}
        onOpenAutoFocus={(e) => e.preventDefault()} // Mantém foco no input
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        <Command shouldFilter={false} className="bg-transparent">
          {/* Cabeçalho */}
          <div className="flex items-center gap-2 px-3 py-2 border-b bg-muted/30">
            <Zap className="h-4 w-4 text-amber-500" />
            <span className="text-sm font-medium">Ações Rápidas</span>
            <span className="text-xs text-muted-foreground ml-auto">
              Digite para filtrar
            </span>
          </div>
          
          <CommandList className="max-h-60">
            {/* Estado de carregamento */}
            {isLoading && (
              <div className="flex items-center justify-center py-6 gap-2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="text-sm text-muted-foreground">Carregando...</span>
              </div>
            )}

            {/* Estado de erro */}
            {error && !isLoading && (
              <div className="flex items-center justify-center py-6 gap-2 text-destructive">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm">Erro ao carregar ações</span>
              </div>
            )}

            {/* Estado vazio */}
            {!isLoading && !error && filteredRules.length === 0 && (
              <CommandEmpty className="py-6 text-center text-sm text-muted-foreground">
                {searchQuery 
                  ? `Nenhuma ação encontrada para "${searchQuery}"` 
                  : 'Nenhuma ação rápida cadastrada'
                }
              </CommandEmpty>
            )}

            {/* Lista de ações agrupadas por categoria */}
            {!isLoading && !error && filteredRules.length > 0 && (
              <>
                {groupedRules.map((group) => (
                  <CommandGroup
                    key={group.key}
                    heading={
                      <div className="flex items-center gap-1.5">
                        {group.icon ? (
                          <RoutingCategoryIcon
                            name={group.icon}
                            color={group.color || undefined}
                            size={11}
                          />
                        ) : null}
                        <span>{group.name}</span>
                        <span className="text-muted-foreground/70 ml-1">
                          ({group.rules.length})
                        </span>
                      </div>
                    }
                  >
                    {group.rules.map((rule) => (
                      <CommandItem
                        key={rule.id}
                        value={rule.id}
                        onSelect={() => handleExecuteRule(rule)}
                        disabled={executingRuleId === rule.id}
                        className="flex items-center gap-2 cursor-pointer aria-selected:bg-accent"
                      >
                        {executingRuleId === rule.id ? (
                          <Loader2 className="h-4 w-4 animate-spin text-amber-500 flex-shrink-0" />
                        ) : (
                          <Play className="h-4 w-4 text-amber-500 flex-shrink-0" />
                        )}
                        <div className="flex flex-col flex-1 min-w-0">
                          <span className="font-medium truncate">{rule.name}</span>
                          {rule.description && (
                            <span className="text-xs text-muted-foreground truncate">
                              {rule.description}
                            </span>
                          )}
                        </div>
                      </CommandItem>
                    ))}
                  </CommandGroup>
                ))}
              </>
            )}
          </CommandList>
          
          {/* Rodapé com dicas de atalho */}
          <div className="border-t px-3 py-2 text-xs text-muted-foreground flex items-center gap-3 bg-muted/20">
            <span>
              <kbd className="bg-muted px-1.5 py-0.5 rounded text-[10px] font-mono">↑↓</kbd>
              {' '}navegar
            </span>
            <span>
              <kbd className="bg-muted px-1.5 py-0.5 rounded text-[10px] font-mono">Enter</kbd>
              {' '}executar
            </span>
            <span>
              <kbd className="bg-muted px-1.5 py-0.5 rounded text-[10px] font-mono">Esc</kbd>
              {' '}cancelar
            </span>
          </div>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
